import { FocusTrap } from 'focus-trap-react';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { CSSTransition } from 'react-transition-group';

import {
  ViewWrapper,
  ViewWrapperDefaultGrid,
} from '@xing-com/platform-layout-view-wrapper';
import { Portal } from '@xing-com/portal';

import * as Styled from './modal-page.styles';
import {
  modalPageDuration,
  modalPageTransitionName,
} from './modal-page.styles';
import type { ModalPageProps } from './modal-page.types';

const SCROLLABLE_OVERFLOW_VALUES = ['auto', 'overlay', 'scroll'];

const isScrollableElement = (element: HTMLElement): boolean => {
  const { overflowY, overflow } = window.getComputedStyle(element);
  const hasScrollableOverflow =
    SCROLLABLE_OVERFLOW_VALUES.includes(overflowY) ||
    SCROLLABLE_OVERFLOW_VALUES.includes(overflow);

  return hasScrollableOverflow && element.scrollHeight > element.clientHeight;
};

const shouldAllowTouchMove = (
  target: EventTarget | null,
  boundary: HTMLElement | null
): boolean => {
  if (!(target instanceof Node) || !boundary?.contains(target)) {
    return false;
  }

  let currentNode: Node | null = target;

  while (currentNode) {
    if (
      currentNode instanceof HTMLElement &&
      isScrollableElement(currentNode)
    ) {
      return true;
    }

    if (currentNode === boundary) {
      return false;
    }

    currentNode = currentNode.parentNode;
  }

  return false;
};

export const ModalPage = React.forwardRef<HTMLDivElement, ModalPageProps>(
  (
    {
      children,
      closeButton,
      contentClassName,
      focusTrapProps,
      isAnimated,
      isShown,
      noGrid,
      noMargin,
      zIndex,
      ...props
    },
    forwardedRef
  ): JSX.Element => {
    const intl = useIntl();
    const modalRef = React.useRef<HTMLDivElement>(null);
    const resolvedRef = forwardedRef || modalRef;

    const animatedModalPageRef = React.useRef<HTMLDivElement>(null);
    const focusTrapContainerRef = React.useRef<HTMLDivElement>(null);

    // Prevent background scrolling
    React.useEffect(() => {
      if (isShown && isAnimated) {
        const originalOverflow = document.body.style.overflow;
        const originalPosition = document.body.style.position;
        const originalTop = document.body.style.top;
        const originalWidth = document.body.style.width;
        const scrollY = window.scrollY;

        // iOS Safari fix: use position fixed
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';

        // Prevent touch scrolling on iOS
        const preventTouchMove = (e: TouchEvent): void => {
          if (shouldAllowTouchMove(e.target, focusTrapContainerRef.current)) {
            return;
          }

          e.preventDefault();
        };

        document.addEventListener('touchmove', preventTouchMove, {
          passive: false,
        });

        return () => {
          document.body.style.overflow = originalOverflow;
          document.body.style.position = originalPosition;
          document.body.style.top = originalTop;
          document.body.style.width = originalWidth;
          window.scrollTo(0, scrollY);
          document.removeEventListener('touchmove', preventTouchMove);
        };
      }

      return undefined;
    }, [isShown, isAnimated]);

    // Keyboard handling
    React.useEffect(() => {
      const handleKey = (event: KeyboardEvent): void => {
        if (isShown && closeButton && event.key === 'Escape') {
          event.preventDefault();
          closeButton.onClick?.();
        }
      };

      document.addEventListener('keydown', handleKey);

      return () => {
        document.removeEventListener('keydown', handleKey);
      };
    }, [closeButton, isShown]);

    const { focusTrapOptions: userFocusTrapOptions, ...otherFocusTrapProps } =
      focusTrapProps || {};
    const mergedFocusTrapOptions = {
      escapeDeactivates: Boolean(closeButton),
      fallbackFocus: () => focusTrapContainerRef.current || document.body,
      returnFocusOnDeactivate: true,
      ...userFocusTrapOptions,
    };

    const content = (
      <ViewWrapper
        contentClassName={contentClassName}
        data-xds="ModalPage"
        {...props}
      >
        {closeButton ? (
          <Styled.CloseButton
            aria-label={intl.formatMessage({
              id: 'BUTTON_CLOSEWINDOW',
              defaultMessage: 'Close window',
            })}
            data-qa="close"
            data-testid="close"
            size="large"
            {...closeButton}
          />
        ) : undefined}
        <Styled.Container
          $noMargin={noMargin}
          aria-modal={isAnimated ? 'true' : undefined}
          ref={resolvedRef}
          role={isAnimated ? 'dialog' : undefined}
          tabIndex={isAnimated ? -1 : undefined}
        >
          {children}
        </Styled.Container>
      </ViewWrapper>
    );

    const wrappedContent = noGrid ? (
      content
    ) : (
      <ViewWrapperDefaultGrid>{content}</ViewWrapperDefaultGrid>
    );

    if (isAnimated) {
      return (
        <Portal>
          <CSSTransition
            appear={isShown}
            classNames={modalPageTransitionName}
            in={isShown}
            key="modalPage"
            mountOnEnter
            timeout={parseInt(modalPageDuration)}
            unmountOnExit
            nodeRef={animatedModalPageRef}
          >
            <Styled.AnimatedModalPage
              ref={animatedModalPageRef}
              $zIndex={zIndex}
            >
              <FocusTrap
                active={isShown}
                {...otherFocusTrapProps}
                focusTrapOptions={mergedFocusTrapOptions}
              >
                <div ref={focusTrapContainerRef} tabIndex={-1}>
                  {wrappedContent}
                </div>
              </FocusTrap>
            </Styled.AnimatedModalPage>
          </CSSTransition>
        </Portal>
      );
    }

    return wrappedContent;
  }
);

ModalPage.displayName = 'ModalPage';
